<template>
  <div class="edmodel-wrap">
    <p class="block-subtitle mb-6">{{ $t('educational_model.subtitle') }}</p>

    <div v-if="loading" class="d-flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <!-- Tabla con scroll horizontal en pantallas pequeñas -->
      <div class="table-scroll">
        <v-simple-table class="edmodel-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="aspect-col">{{ $t('educational_model.col_aspect') }}</th>
                <th class="question-col">
                  <v-icon x-small color="primary" class="mr-1">mdi-emoticon-outline</v-icon>
                  {{ $t('educational_model.col_comfort') }}
                </th>
                <th class="question-col">
                  <v-icon x-small color="primary" class="mr-1">mdi-hand-heart-outline</v-icon>
                  {{ $t('educational_model.col_help') }}
                </th>
                <th class="question-col">
                  <v-icon x-small color="grey" class="mr-1">mdi-account-star-outline</v-icon>
                  {{ $t('educational_model.col_peer') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(group, idx) in questionGroups" :key="group.node_id">
                <td class="aspect-cell">
                  <div class="d-flex align-center">
                    <span class="aspect-number mr-2">{{ idx + 1 }}</span>
                    <span class="aspect-label">{{ group.node_label }}</span>
                  </div>
                </td>
                <td class="input-cell">
                  <v-select
                    v-if="group.qa"
                    v-model="answers[`${group.node_id}_${group.qa.id_question}`]"
                    :items="parseOptions(group.qa)"
                    item-text="texto"
                    item-value="valor"
                    :placeholder="$t('educational_model.select_placeholder')"
                    dense
                    hide-details
                    outlined
                    :disabled="locked"
                    @change="saveAnswer(group.qa, group.node_id)"
                  ></v-select>
                </td>
                <td class="input-cell">
                  <v-select
                    v-if="group.qb"
                    v-model="answers[`${group.node_id}_${group.qb.id_question}`]"
                    :items="parseOptions(group.qb)"
                    item-text="texto"
                    item-value="valor"
                    :placeholder="$t('educational_model.select_placeholder')"
                    dense
                    hide-details
                    outlined
                    :disabled="locked"
                    @change="saveAnswer(group.qb, group.node_id)"
                  ></v-select>
                </td>
                <td class="input-cell">
                  <v-autocomplete
                    v-if="group.qc"
                    v-model="answers[`${group.node_id}_${group.qc.id_question}`]"
                    :items="employeeList"
                    item-text="username"
                    item-value="id"
                    :placeholder="$t('educational_model.peer_placeholder')"
                    dense
                    hide-details
                    outlined
                    clearable
                    :disabled="locked"
                    @change="saveAnswer(group.qc, group.node_id)"
                  ></v-autocomplete>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <!-- Indicador de guardado -->
      <div v-if="saving" class="saving-indicator mt-3">
        <v-progress-linear indeterminate color="primary" height="2" />
        <p class="text-caption text-center mt-1 grey--text">{{ $t('educational_model.saving') }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import { useMainStore } from '@/store/main';
import { mapStores } from 'pinia';

export default {
  name: 'EducationalModelBlock',
  props: {
    networkModeId: { type: Number, required: true },
    cycleId:       { type: Number, required: true },
    locked:        { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      saving:  false,
      questions: [],
      nodes:     [],
      answers:   {},
    };
  },
  computed: {
    ...mapStores(useMainStore),
    employeeList() {
      return this.mainStore.employees;
    },
    questionGroups() {
      const qa = this.questions[0] || null;
      const qb = this.questions[1] || null;
      const qc = this.questions[2] || null;
      return this.nodes.map((node) => ({
        node_id:    node.id_node,
        node_label: node[`Node_${this.$i18n.locale}`] || node.Node_es,
        qa,
        qb,
        qc,
      }));
    },
    isComplete() {
      if (!this.questions.length || !this.nodes.length) return false;
      const qa = this.questions[0];
      const qb = this.questions[1];
      if (!qa || !qb) return false;
      return this.nodes.every(node => {
        const vA = this.answers[`${node.id_node}_${qa.id_question}`];
        const vB = this.answers[`${node.id_node}_${qb.id_question}`];
        return (vA !== null && vA !== undefined && vA !== '') &&
               (vB !== null && vB !== undefined && vB !== '');
      });
    },
  },
  watch: {
    isComplete(val) {
      this.$emit('completion-changed', val);
    },
  },
  async mounted() {
    await Promise.all([this.loadQuestions(), this.loadNodes(), this.loadExistingAnswers()]);
    this.loading = false;
    this.$emit('completion-changed', this.isComplete);
  },
  methods: {
    async loadQuestions() {
      const resp = await this.$axios.get(
        `${process.env.VUE_APP_BACKEND_URL}/network_mode/${this.networkModeId}/questions`
      );
      this.questions = resp.data;
    },
    async loadNodes() {
      const resp = await this.$axios.get(
        `${process.env.VUE_APP_BACKEND_URL}/network_mode/${this.networkModeId}/nodes`
      );
      this.nodes = resp.data;
    },
    async loadExistingAnswers() {
      try {
        const resp = await this.$axios.get(
          `${process.env.VUE_APP_BACKEND_URL}/user/${this.mainStore.logged_user.id}/cycle/${this.cycleId}/responses`
        );
        resp.data.forEach(r => {
          if (r.adjacency_input_form.id_network_mode === this.networkModeId) {
            try {
              const parsed = JSON.parse(r.Response);
              parsed.forEach(item => {
                this.$set(this.answers, `${item.item_id}_${r.id_question}`, item.valor);
              });
            } catch (e) { void e; }
          }
        });
      } catch (e) { void e; }
    },
    parseOptions(question) {
      try {
        return JSON.parse(
          question.question_possible_answers[`Question_possible_answers_${this.$i18n.locale}`]
        );
      } catch { return []; }
    },
    async saveAnswer(question, nodeId) {
      this.saving = true;
      const value = this.answers[`${nodeId}_${question.id_question}`];
      try {
        await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/save_answer`, {
          cycle_id:        this.cycleId,
          user_email:      this.mainStore.logged_user.email,
          item_id:         nodeId,
          question_id:     question.id_question,
          network_mode_id: this.networkModeId,
          selected_option: value,
        });
      } catch (e) { console.error(e); }
      this.saving = false;
    },
  },
};
</script>

<style scoped>
.edmodel-wrap   { padding: 4px 0 24px; }
.block-subtitle { font-size: 0.9rem; color: #4a5568; }

/* ── Tabla ───────────────────────────────────── */
.table-scroll { overflow-x: auto; }
.edmodel-table { min-width: 700px; }

.edmodel-table ::v-deep .v-data-table__wrapper {
  overflow: visible !important;
}

.aspect-col {
  min-width: 180px;
  background: #f0f4f8 !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  color: #1e3a5f !important;
  white-space: nowrap;
}
.question-col {
  min-width: 210px;
  font-size: 0.8rem !important;
  color: #374151 !important;
  font-weight: 600 !important;
  line-height: 1.3;
}
.aspect-cell {
  background: #f0f4f8;
  vertical-align: middle;
  padding: 10px 16px !important;
}
.input-cell {
  vertical-align: middle;
  padding: 8px 10px !important;
  min-width: 210px;
}
.aspect-number {
  min-width: 24px; height: 24px;
  background: #1e3a5f;
  color: white;
  border-radius: 50%;
  font-size: 0.76rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.aspect-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e3a5f;
  line-height: 1.3;
}

.saving-indicator { max-width: 300px; margin: 0 auto; }
</style>
